import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import * as React from 'react';
import { c as classNames, l as liveAnnounce } from '../theme-provider/index-chunk.esm.js';
import { E as EGDSCheckbox } from '../checkbox-group/index-chunk.esm.js';
import { useLocalization } from '@shared-ui/localization-context';
import styles from '../styles/uitk-egds-tables.css';
import { a as EGDSIcon, E as EGDSIconSize } from '../icon/index-chunk.esm.js';
import '../paging-button/index-chunk.esm.js';
import { E as EGDSSecondaryButton } from '../secondary-button/index-chunk.esm.js';
import { v as validatePropType } from '../validate-prop-types/index-chunk.esm.js';

const EGDSTableCellTextAlignsMap = [
    ['left', 'textalign-left'],
    ['right', 'textalign-right'],
    ['center', 'textalign-center'],
    ['justify', 'textalign-justify'],
    ['justify-all', 'textalign-justify-all'],
    ['start', 'textalign-start'],
    ['end', 'textalign-end'],
    ['match-parent', 'textalign-match-parent'],
];
const EGDSTableCellTextAlign = new Map(EGDSTableCellTextAlignsMap);
const EGDSTableColInlineSizeMap = [
    ['10%', 'size-10-percent'],
    ['20%', 'size-20-percent'],
    ['30%', 'size-30-percent'],
    ['40%', 'size-40-percent'],
    ['50%', 'size-50-percent'],
    ['60%', 'size-60-percent'],
    ['70%', 'size-70-percent'],
    ['80%', 'size-80-percent'],
    ['90%', 'size-90-percent'],
    ['100%', 'size-100-percent'],
];
const EGDSTableColInlineSize = new Map(EGDSTableColInlineSizeMap);
const EGDSTableCellVerticalAlignsMap = [
    ['baseline', 'verticalalign-baseline'],
    ['sub', 'verticalalign-sub'],
    ['super', 'verticalalign-super'],
    ['text-top', 'verticalalign-text-top'],
    ['text-bottom', 'verticalalign-text-bottom'],
    ['middle', 'verticalalign-middle'],
    ['top', 'verticalalign-top'],
    ['bottom', 'verticalalign-bottom'],
];
const EGDSTableCellVerticalAlign = new Map(EGDSTableCellVerticalAlignsMap);
const EGDSTableRowsStripingMap = [
    ['odd', 'striped-odd'],
    ['even', 'striped-even'],
];
const EGDSTableRowsStriping = new Map(EGDSTableRowsStripingMap);
const EGDSTableOverflowMap = [['inline-scroll', 'inline-scroll']];
const EGDSTableOverflow = new Map(EGDSTableOverflowMap);
const EGDSTableColumnSizeMap = [['equal', 'col-inlinesize-equal']];
const EGDSTableColumnSize = new Map(EGDSTableColumnSizeMap);
const EGDSTableCellTag = {
    header: 'th',
    data: 'td',
};
const EGDSTableCellBorder = {
    horizontal: 'horizontal',
    vertical: 'vertical',
};
const EGDSTableCellHorizontalAlignmentMap = [
    ['leading', 'leading'],
    ['center', 'center'],
    ['trailing', 'trailing'],
];
const EGDSTableCellHorizontalAlignment = new Map(EGDSTableCellHorizontalAlignmentMap);
const EGDSTableCellVerticalAlignmentMap = [
    ['top', 'top'],
    ['middle', 'middle'],
    ['bottom', 'bottom'],
];
const EGDSTableCellVerticalAlignment = new Map(EGDSTableCellVerticalAlignmentMap);
const EGDSTableCellPadding = {
    standard: 'standard',
    graphic: 'graphic',
    zero: 'zero',
};
const EGDSTableSortIconName = {
    ascending: 'sort_ascending',
    descending: 'sort_descending',
    default: 'swap_vert',
};
const EGDSTableSortDirectionMap = [
    ['ascending', 'ascending'],
    ['descending', 'descending'],
];
const EGDSTableSortDirection = new Map(EGDSTableSortDirectionMap);
const EGDSTableFrozenColumnDirectionMap = [
    ['leading', 'leading'],
    ['trailing', 'trailing'],
];
const EGDSTableFrozenColumnDirection = new Map(EGDSTableFrozenColumnDirectionMap);
const EGDSTableFrozenRowMap = [
    ['headerRow', 'headerRow'],
    ['bodyRow', 'bodyRow'],
];
const EGDSTableFrozenRow = new Map(EGDSTableFrozenRowMap);
const EGDSTablefrozenRegionsStyleMap = [
    ['columnGradient-rowBorder', 'columnGradient-rowBorder'],
    ['columnGradient-rowNoBorder', 'columnGradient-rowNoBorder'],
    ['columnBorder-rowGradient', 'columnBorder-rowGradient'],
    ['columnNoBorder-rowGradient', 'columnNoBorder-rowGradient'],
    ['onlyBorders', 'onlyBorders'],
];
const EGDSTableFrozenRegionsStyle = new Map(EGDSTablefrozenRegionsStyleMap);
const EGDSTableCaptionAlignmentMap = [
    ['start', 'start'],
    ['end', 'end'],
];
const EGDSTableCaptionAlignment = new Map(EGDSTableCaptionAlignmentMap);
const EGDSTableCellHeaderScope = {
    column: 'col',
    rowColumn: 'row',
};
const EGDSTableCellHighlight = {
    highlight: 'highlight',
    highlightAlternate: 'highlight-alternate',
};

const getClassNameList$7 = classNames.bind(styles);
const EGDSTableBody = (props) => {
    const { children, className, frozenRow } = props;
    const baseClassName = 'uitk-table-body';
    return (jsx("tbody", { className: getClassNameList$7(className, {
            [`${baseClassName}-frozen`]: frozenRow === 'bodyRow',
        }), children: children }));
};
EGDSTableBody.displayName = 'EGDSTableBody';

const getClassNameList$6 = classNames.bind(styles);
const EGDSTableCaption = (props) => {
    const { align, children, className, ...attributes } = props;
    const baseClassName = 'uitk-table-caption';
    const captionClassNames = getClassNameList$6(baseClassName, className, align && `${baseClassName}-${EGDSTableCaptionAlignment.get(align)}`);
    return children ? (jsx("caption", { className: captionClassNames, ...attributes, children: children })) : null;
};
EGDSTableCaption.displayName = 'EGDSTableCaption';
EGDSTableCaption.defaultProps = {
    align: EGDSTableCaptionAlignment.get('start'),
};

const EGDSTableCellContext = React.createContext({});

const getClassNameList$5 = classNames.bind(styles);
const EGDSTableCell = (props) => {
    const cellContext = React.useContext(EGDSTableCellContext);
    const { formatText } = useLocalization();
    const { align, border, children, className, columnWidth, colSpan, description, isCellExpandable, id, frozenColumn, frozenRegionsStyle, fixedWidthColumn, highlight, onClick, onSort, padding, isSortable, initialSortDirection, scope, striped, tag: Tag = cellContext.tag ?? EGDSTableCellTag.data, } = props;
    const [sortDirection, setSortDirection] = React.useState(initialSortDirection);
    const tableContext = React.useContext(EGDSTableContext);
    const baseClass = 'uitk-table-cell';
    const isHeaderCell = Tag === EGDSTableCellTag.header || cellContext.tag === EGDSTableCellTag.header;
    const borderClasses = border &&
        (Array.isArray(border)
            ? [...border.map((borderDirection) => `${baseClass}-border-${borderDirection}`)]
            : `${baseClass}-border-${border}`);
    const cellClassNames = getClassNameList$5(baseClass, borderClasses, `${baseClass}-padding-${padding}`, align?.horizontal && `${baseClass}-align-${align.horizontal}`, align?.vertical && `${baseClass}-align-${align.vertical}`, {
        [`${baseClass}-striped`]: striped,
        [`${baseClass}-header`]: isHeaderCell,
        [`${baseClass}-header-sorting`]: isSortable,
        [`${baseClass}-header-sorting-${sortDirection}`]: isSortable && sortDirection,
        [`${baseClass}-frozen-${EGDSTableFrozenColumnDirection.get(frozenColumn)}`]: frozenColumn,
        [`${baseClass}-frozen-${EGDSTableFrozenColumnDirection.get(frozenColumn)}-gradient`]: frozenColumn &&
            frozenRegionsStyle !== 'columnBorder-rowGradient' &&
            frozenRegionsStyle !== 'columnNoBorder-rowGradient' &&
            frozenRegionsStyle !== 'onlyBorders',
        [`${baseClass}-frozen-${EGDSTableFrozenColumnDirection.get(frozenColumn)}-border`]: frozenColumn && (frozenRegionsStyle === 'columnBorder-rowGradient' || frozenRegionsStyle === 'onlyBorders'),
        [`${baseClass}-fixed-width-column`]: fixedWidthColumn,
        [`${baseClass}-expansion`]: isCellExpandable,
        [`${baseClass}-${highlight}`]: highlight,
    }, className);
    const getSortIconClasses = (direction) => getClassNameList$5(`${baseClass}-icon`, {
        [`${baseClass}-icon-hidden`]: sortDirection !== direction,
    });
    React.useEffect(() => {
        if (tableContext.sortedColumn && tableContext.sortedColumn !== id?.toString()) {
            setSortDirection(undefined);
        }
    }, [tableContext?.sortedColumn, id]);
    const onClickHandler = (e) => {
        if (isSortable) {
            const getNewSortDirection = (dir) => dir === EGDSTableSortDirection.get('ascending')
                ? EGDSTableSortDirection.get('descending')
                : EGDSTableSortDirection.get('ascending');
            onSort?.(getNewSortDirection(sortDirection), id);
            tableContext?.onSort?.(getNewSortDirection(sortDirection), id);
            setSortDirection((prevState) => {
                const newDirection = getNewSortDirection(prevState);
                const announcement = newDirection === EGDSTableSortDirection.get('ascending')
                    ? formatText('table.sorted.a11yLabel.ascended')
                    : formatText('table.sorted.a11yLabel.descended');
                liveAnnounce(announcement);
                return newDirection;
            });
        }
        onClick?.(e);
    };
    const randomId = Math.random().toString(36).substring(7);
    const uniqueLabelId = `aria-label-${randomId}`;
    return isHeaderCell && isSortable ? (jsx(Tag, { className: cellClassNames, style: { minInlineSize: columnWidth }, scope: scope, children: jsxs("button", { "aria-label": sortDirection === EGDSTableSortDirection.get('ascending')
                ? formatText('table.sort.a11yLabel.descending')
                : formatText('table.sort.a11yLabel.ascending'), type: "button", className: getClassNameList$5('uitk-table-header-button'), onClick: onClickHandler, children: [children, !sortDirection && jsx(EGDSIcon, { size: EGDSIconSize.EXTRA_SMALL, name: EGDSTableSortIconName.default }), jsx(EGDSIcon, { size: EGDSIconSize.EXTRA_SMALL, name: EGDSTableSortIconName.ascending, className: getSortIconClasses(EGDSTableSortDirection.get('ascending')) }), jsx(EGDSIcon, { size: EGDSIconSize.EXTRA_SMALL, name: EGDSTableSortIconName.descending, className: getSortIconClasses(EGDSTableSortDirection.get('descending')) })] }) })) : (jsxs(Tag, { className: cellClassNames, colSpan: colSpan, style: { minInlineSize: isHeaderCell ? columnWidth : '' }, scope: isHeaderCell ? scope : undefined, "aria-describedby": description ? uniqueLabelId : undefined, children: [children, description && (jsx("span", { className: "is-visually-hidden", "aria-hidden": true, id: uniqueLabelId, children: description }))] }));
};
const defaultProps = {
    align: {
        horizontal: 'leading',
        vertical: 'middle',
    },
    padding: EGDSTableCellPadding.standard,
    scope: EGDSTableCellHeaderScope.column,
};
EGDSTableCell.defaultProps = defaultProps;
EGDSTableCell.displayName = 'EGDSTableCell';

const getClassNameList$4 = classNames.bind(styles);
const EGDSTableRow = (props) => {
    const { children, checkboxA11yLabel, isChecked, className, isRowExpandable, onRowSelect, rowId, isSelectable, expandRowA11yLabel, expansionContent, expansionCellBorder, ...attributes } = props;
    const l10n = useLocalization();
    const [checkedRow, setCheckedRow] = React.useState(isChecked);
    const tableContext = React.useContext(EGDSTableContext);
    const [visible, setVisible] = React.useState(false);
    const rowRef = React.useRef(null);
    const cellContext = React.useContext(EGDSTableCellContext);
    const isHeaderRow = children &&
        React.Children.toArray(children).some((child) => child.props?.tag === EGDSTableCellTag.header || cellContext.tag === EGDSTableCellTag.header);
    const isMenuRow = children &&
        React.Children.toArray(children).some((child) => child.props?.children?.props?.isOpen === true);
    React.useEffect(() => {
        setCheckedRow(isChecked);
    }, [isChecked]);
    const baseRowClass = 'uitk-table-row';
    const rowClasses = getClassNameList$4(baseRowClass, className, {
        [`${baseRowClass}-selected`]: checkedRow,
        [`${baseRowClass}-menu`]: isMenuRow,
        [`${baseRowClass}-has-expansion-trigger`]: isRowExpandable && !isHeaderRow,
        [`${baseRowClass}-has-expansion-trigger-active`]: isRowExpandable && visible && !isHeaderRow,
    });
    const expansionContentClasses = getClassNameList$4(`${baseRowClass}-expansion-content`);
    const expandoTriggerIconClasses = getClassNameList$4(`uitk-table-expando-trigger-icon${visible ? ' uitk-table-expando-trigger-icon-active' : ''}`);
    const onCheck = (e) => {
        typeof rowId === 'number' && onRowSelect?.(rowId, e.currentTarget.checked);
        setCheckedRow(e.currentTarget.checked);
    };
    const checkboxCell = (jsx(EGDSTableCell, { className: getClassNameList$4('uitk-table-cell-action'), children: jsx(EGDSCheckbox, { checked: checkedRow, labelClassName: "is-visually-hidden", onChange: onCheck, id: rowId?.toString(), a11yLabel: checkboxA11yLabel }) }));
    const expandoTriggerIcon = (jsx(EGDSIcon, { className: expandoTriggerIconClasses, "data-testid": visible ? 'uitk-table-expando-button-icon-expanded' : undefined, name: "expand_more" }));
    const expansionClickHandler = React.useCallback(() => {
        setVisible(!visible);
    }, [visible]);
    const expansionCell = (jsx(EGDSTableCell, { isCellExpandable: true, padding: EGDSTableCellPadding.graphic, border: expansionCellBorder, children: jsx(EGDSSecondaryButton, { "aria-expanded": visible, "aria-controls": rowId?.toString(), "aria-label": expandRowA11yLabel ?? l10n.formatText('table.expand.row', rowId === undefined ? '' : rowId + 1), onClick: expansionClickHandler, children: expandoTriggerIcon }) }));
    const getExpansionCellMarkup = () => {
        if (isHeaderRow) {
            return jsx(EGDSTableCell, { isCellExpandable: true, padding: EGDSTableCellPadding.graphic, border: expansionCellBorder });
        }
        if (isRowExpandable) {
            return expansionCell;
        }
    };
    const getExpansionContentRow = () => (jsx("tr", { className: rowClasses, ref: rowRef, children: jsx(EGDSTableCell, { className: getClassNameList$4('uitk-table-cell-expandable'), colSpan: tableContext.colSpan ?? 100, border: visible ? expansionCellBorder : undefined, children: jsx("div", { className: expansionContentClasses, children: expansionContent }) }) }));
    const expandableRow = (jsxs(Fragment, { children: [jsxs("tr", { className: rowClasses, ref: rowRef, ...attributes, children: [getExpansionCellMarkup(), children] }), expansionContent && getExpansionContentRow()] }));
    return tableContext.expandableRows || isRowExpandable ? (expandableRow) : (jsxs("tr", { className: rowClasses, ...attributes, children: [isSelectable && checkboxCell, children] }));
};
EGDSTableRow.displayName = 'EGDSTableRow';

const getClassNameList$3 = classNames.bind(styles);
const EGDSTableHead = (props) => {
    const { children, className, frozenRow, frozenRegionsStyle, ...attributes } = props;
    const baseClassName = 'uitk-table-head';
    const headClassNames = getClassNameList$3(baseClassName, className, {
        [`${baseClassName}-frozen`]: frozenRow === 'headerRow',
        [`${baseClassName}-frozen-gradient`]: frozenRow === 'headerRow' &&
            frozenRegionsStyle !== 'columnGradient-rowBorder' &&
            frozenRegionsStyle !== 'columnGradient-rowNoBorder' &&
            frozenRegionsStyle !== 'onlyBorders',
        [`${baseClassName}-frozen-border`]: frozenRow === 'headerRow' &&
            (frozenRegionsStyle === 'columnGradient-rowBorder' || frozenRegionsStyle === 'onlyBorders'),
    });
    const tableCellContextProviderValue = React.useMemo(() => ({ tag: EGDSTableCellTag.header }), []);
    return children ? (jsx(EGDSTableCellContext.Provider, { value: tableCellContextProviderValue, children: jsx("thead", { className: headClassNames, ...attributes, children: children }) })) : null;
};
EGDSTableHead.displayName = 'EGDSTableHead';

const customHeightType = {
    FIXED: 'fixed',
    RELATIVE: 'relative',
};
const getClassNameList$2 = classNames.bind(styles);
const EGDSTableContext = React.createContext({});
const EGDSTable = React.forwardRef((props, ref) => {
    const { border, children, className, caption, customHeight, expandableRows, frozenColumn, frozenRow, frozenRegionsStyle, fixedWidthColumns, onSort, onSingleRowSelect, onAllRowsSelect, overflow, isRounded, clipContents, selectableRows, striped, summary, data, ...attributes } = props;
    const [tableData, setTableData] = React.useState(data);
    const [sortedColumn, setSortedColumn] = React.useState('');
    const [scrollPosition, setScrollPosition] = React.useState('');
    const [canScroll, setCanScroll] = React.useState(false);
    const tableContainer = React.useRef(null);
    const tableElement = React.useRef(null);
    const getScrollPosition = React.useCallback(() => {
        if (tableContainer && tableContainer.current && canScroll) {
            const exactScrollPosition = tableContainer.current.scrollLeft;
            if (exactScrollPosition === 0) {
                setScrollPosition('start');
            }
            else if (exactScrollPosition + tableContainer.current.offsetWidth >= tableContainer.current.scrollWidth) {
                setScrollPosition('end');
            }
            else {
                setScrollPosition('middle');
            }
        }
    }, [canScroll]);
    React.useEffect(() => {
        if (tableContainer?.current && tableElement?.current) {
            setCanScroll(tableElement.current.clientWidth > tableContainer.current.clientWidth);
        }
        getScrollPosition();
        setTableData(data);
    }, [data, getScrollPosition]);
    const baseCssClass = 'uitk-table';
    const tableContainerClasses = getClassNameList$2(`${baseCssClass}-container`, {
        [`${baseCssClass}-container-overflow-inline-scroll`]: overflow,
        [`${baseCssClass}-container-scroll-${scrollPosition}`]: canScroll,
        [`${baseCssClass}-container-${frozenColumn}-frozen-column`]: frozenColumn,
    });
    const tableClasses = getClassNameList$2(baseCssClass, {
        [`${baseCssClass}-border`]: border,
        [`${baseCssClass}-rounded`]: isRounded,
        [`${baseCssClass}-clip-contents`]: clipContents,
        [`${baseCssClass}-fixed-width-columns`]: fixedWidthColumns,
    }, striped && `${baseCssClass}-${EGDSTableRowsStriping.get(striped)}`, className);
    const l10n = useLocalization();
    const [selectedRows, setSelectedRows] = React.useState([]);
    const [indeterminate, setIndeterminate] = React.useState(false);
    const selectAllCheckboxId = 'SelectAllRowsCheckbox';
    const getSelectedRows = () => selectedRows;
    React.useImperativeHandle(ref, () => ({
        getSelectedRows,
    }));
    const onSelectAllRows = (e) => {
        const allOrNoRows = tableData && (indeterminate || e.currentTarget.checked) ? tableData.rows : [];
        setSelectedRows(allOrNoRows);
        setIndeterminate(false);
        onAllRowsSelect?.(allOrNoRows);
    };
    const dataCols = tableData &&
        tableData.columns.map((column, index) => {
            const columnWidth = (fixedWidthColumns && column.width);
            return (jsx(EGDSTableCell, { tag: EGDSTableCellTag.header, id: column.fieldName, columnWidth: columnWidth, frozenColumn: (((frozenColumn === 'leading' && index === 0) ||
                    (frozenColumn === 'trailing' && index === tableData.columns.length - 1)) &&
                    frozenColumn), frozenRegionsStyle: frozenRegionsStyle, ...column, children: column.headerName }, column.fieldName));
        });
    if (selectableRows && Array.isArray(dataCols) && dataCols.length) {
        const isChecked = selectedRows?.length === tableData?.rows?.length || indeterminate;
        dataCols?.unshift(jsx(EGDSTableCell, { tag: EGDSTableCellTag.header, className: getClassNameList$2('uitk-table-cell-action'), children: jsx(EGDSCheckbox, { checked: isChecked, indeterminate: indeterminate, onChange: onSelectAllRows, id: selectAllCheckboxId, a11yLabel: !isChecked || indeterminate
                    ? l10n.formatText('table.select.rows')
                    : l10n.formatText('table.deselect.rows') }) }));
    }
    const getIndeterminate = (totalRowsSelected) => totalRowsSelected > 0 && totalRowsSelected !== tableData?.rows?.length;
    const onRowSelect = (rowId, checked) => {
        const currentRow = tableData?.rows.filter((selectedRow) => selectedRow.id === rowId);
        const updatedSelectedRows = checked
            ? [...selectedRows, ...currentRow] // Add row to state
            : selectedRows?.filter((selectedRow) => rowId !== selectedRow.id); // Delete row from state
        setSelectedRows(updatedSelectedRows);
        setIndeterminate(getIndeterminate(updatedSelectedRows.length));
        onSingleRowSelect?.(updatedSelectedRows);
    };
    const dataRows = tableData &&
        tableData.rows.map((row, index) => (jsx(Fragment, { children: jsx(EGDSTableRow, { isChecked: selectedRows?.includes(row), rowId: row.id, isSelectable: selectableRows, onRowSelect: selectableRows ? onRowSelect : undefined, isRowExpandable: expandableRows && row.expansionContent !== undefined, checkboxA11yLabel: row.checkboxA11yLabel, expandRowA11yLabel: row.expandRowA11yLabel, expansionContent: row.expansionContent, children: tableData.columns.map((column, j) => {
                    if (((frozenColumn === 'leading' && j === 0) ||
                        (frozenColumn === 'trailing' && j === tableData.columns.length - 1)) &&
                        frozenColumn) {
                        return (jsxs(EGDSTableCell, { id: column.fieldName, fixedWidthColumn: fixedWidthColumns, frozenColumn: frozenColumn, frozenRegionsStyle: frozenRegionsStyle, children: [React.isValidElement(row[column.fieldName]), row[column.fieldName]] }, column.fieldName));
                    }
                    return React.isValidElement(row[column.fieldName]) ? (row[column.fieldName]) : (jsxs(EGDSTableCell, { id: column.fieldName, fixedWidthColumn: fixedWidthColumns, children: [React.isValidElement(row[column.fieldName]), row[column.fieldName]] }, column.fieldName));
                }) }, index) })));
    const dataChildren = tableData && tableData.columns.length && (jsxs(Fragment, { children: [jsx(EGDSTableHead, { frozenRow: frozenRow, frozenRegionsStyle: frozenRegionsStyle, children: jsx(EGDSTableRow, { children: dataCols }) }), jsx(EGDSTableBody, { children: dataRows })] }));
    // For tables which have a frozen region, make the table focusable.
    // This allows our keyboard-only users in Chrome to use arrow keys to view offscreen content. (Firefox contains logic for this.)
    const tabIndex = frozenColumn || frozenRow ? 0 : -1;
    const blockSize = frozenRow && validatePropType(customHeight?.type, customHeightType)
        ? { blockSize: `${customHeight?.value}${customHeight?.type === customHeightType.FIXED ? 'px' : 'vh'}` }
        : {};
    const handleOnSort = (direction, id) => {
        setSortedColumn(id.toString());
        if (onSort)
            onSort(direction, id);
    };
    const tableChildren = dataChildren ?? children;
    const tableContextProviderValue = React.useMemo(() => ({
        onSort: handleOnSort,
        sortedColumn,
        expandableRows,
        colSpan: expandableRows && tableData?.columns ? tableData.columns.length + 1 : tableData?.columns.length,
        selectableRows,
    }), [onSort, sortedColumn, expandableRows, selectableRows]);
    if (!tableChildren) {
        return null;
    }
    return (jsx(EGDSTableContext.Provider, { value: tableContextProviderValue, children: jsx("div", { className: `${baseCssClass}-relative-container`, children: jsx("div", { ref: tableContainer, className: tableContainerClasses, tabIndex: tabIndex, style: blockSize, onScroll: getScrollPosition, children: jsxs("table", { ref: tableElement, summary: summary, className: tableClasses, ...attributes, children: [caption && jsx(EGDSTableCaption, { children: caption }), tableChildren] }) }) }) }));
});
EGDSTable.defaultProps = {
    overflow: true,
};
EGDSTable.displayName = 'EGDSTable';

const getClassNameList$1 = classNames.bind(styles);
const EGDSTableCol = (props) => {
    const { className, inlineSize, ...attributes } = props;
    const baseClassName = 'uitk-table-col';
    const colClassNames = getClassNameList$1(baseClassName, inlineSize && `${baseClassName}-${EGDSTableColInlineSize.get(inlineSize)}`, className);
    return jsx("col", { className: colClassNames, ...attributes });
};
EGDSTableCol.displayName = 'EGDSTableCol';

const getClassNameList = classNames.bind(styles);
const EGDSTableColGroup = (props) => {
    const { children, ...attributes } = props;
    return children ? (jsx("colgroup", { className: getClassNameList('uitk-table-colgroup'), ...attributes, children: children })) : null;
};
EGDSTableColGroup.displayName = 'EGDSTableColGroup';

export { EGDSTableFrozenRow as A, EGDSTableFrozenRegionsStyle as B, EGDSTableCaptionAlignment as C, EGDSTableCellHeaderScope as D, EGDSTableContext as E, EGDSTableCellHighlight as F, EGDSTable as a, EGDSTableCell as b, customHeightType as c, EGDSTableCol as d, EGDSTableColGroup as e, EGDSTableBody as f, EGDSTableCaption as g, EGDSTableHead as h, EGDSTableRow as i, EGDSTableCellTextAlign as j, EGDSTableColInlineSize as k, EGDSTableCellVerticalAlign as l, EGDSTableRowsStriping as m, EGDSTableOverflow as n, EGDSTableColumnSize as o, EGDSTableCellTag as p, EGDSTableCellBorder as q, EGDSTableCellHorizontalAlignmentMap as r, EGDSTableCellHorizontalAlignment as s, EGDSTableCellVerticalAlignmentMap as t, EGDSTableCellVerticalAlignment as u, EGDSTableCellPadding as v, EGDSTableSortIconName as w, EGDSTableSortDirectionMap as x, EGDSTableSortDirection as y, EGDSTableFrozenColumnDirection as z };
//# sourceMappingURL=index-chunk.esm.js.map
